// refresh v3 - ensure lovable auth module is loaded at startup for OAuth callback handling
import "@/integrations/lovable/index";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";

import CinematicLanding from "./pages/CinematicLanding";
import Overview from "./pages/Overview";
import Vault from "./pages/Vault";
import SettingsPage from "./pages/SettingsPage";
// AdminStats removed from frontend routing to prevent email harvesting vectors
import Login from "./pages/Login";
import ResetPassword from "./pages/ResetPassword";
import Legal from "./pages/Legal";
import Privacy from "./pages/Privacy";
import Terms from "./pages/Terms";
import Refunds from "./pages/Refunds";
import About from "./pages/About";
import Contact from "./pages/Contact";
// AdminDashboard removed - using AdminPortal only
import AdminPortal from "./pages/AdminPortal";
import AdminAssetDetail from "./pages/AdminAssetDetail";
import AdminIntelligence from "./pages/AdminIntelligence";
import AdminLab from "./pages/AdminLab";
import { lazy, Suspense } from "react";
const AdminAnalytics = lazy(() => import("./pages/AdminAnalytics"));
import TrendsDashboard from "./pages/TrendsDashboard";
import FreshBilling from "./pages/FreshBilling";
import Professionals from "./pages/Professionals";
import Pricing from "./pages/Pricing";
import SocialWarRoom from "./pages/SocialWarRoom";
import ContentLibrary from "./pages/ContentLibrary";
import Library from "./pages/Library";
import AnalysisResult from "./pages/AnalysisResult";
import NeutralizerLabDetail from "./pages/NeutralizerLabDetail";
import Reports from "./pages/Reports";
import RehearsalChat from "./pages/RehearsalChat";
import RehearsalDebrief from "./pages/RehearsalDebrief";
import CreatorPortal from "./pages/CreatorPortal";
import Practice from "./pages/Practice";
import ArchetypeArchive from "./pages/ArchetypeArchive";
import RehearsalArchive from "./pages/RehearsalArchive";
import FreeAnalysisResult from "./pages/FreeAnalysisResult";
import GreyRockMethodExamples from "./pages/GreyRockMethodExamples";
import AiDetectNarcissisticAbuse from "./pages/AiDetectNarcissisticAbuse";
import ChatgptVsGreyrockAi from "./pages/ChatgptVsGreyrockAi";
import FreeGaslightingDetector from "./pages/FreeGaslightingDetector";
import WhyExhaustedAfterConversations from "./pages/WhyExhaustedAfterConversations";
import WhyILookLikeTheCrazyOne from "./pages/WhyILookLikeTheCrazyOne";
import WhyDoTheyDisappearAndComeBack from "./pages/WhyDoTheyDisappearAndComeBack";
import WhyWalkingOnEggshells from "./pages/WhyWalkingOnEggshells";
import ReactiveAbuseExamples from "./pages/ReactiveAbuseExamples";
import DarvoExamples from "./pages/DarvoExamples";
import EmotionalInvalidationExamples from "./pages/EmotionalInvalidationExamples";
import ManipulativeTextMessageAnalysis from "./pages/ManipulativeTextMessageAnalysis";
import ArticulationBeforeAdjudication from "./pages/ArticulationBeforeAdjudication";
import OverthinkingVsPatternRecognition from "./pages/OverthinkingVsPatternRecognition";
import CriticismPatternsInTextMessages from "./pages/analysis/CriticismPatternsInTextMessages";
import InvalidationPatternsInTexts from "./pages/analysis/InvalidationPatternsInTexts";
import DarvoInTextMessages from "./pages/analysis/DarvoInTextMessages";
import Resources from "./pages/Resources";
import SpotThePattern from "./pages/SpotThePattern";
import { useEffect } from "react";
import { flushAnonymousEvents, trackEvent } from "@/lib/analytics";
import { getAttribution, applyShortLinkAttribution } from "@/lib/attribution";
import { supabase } from "@/integrations/supabase/client";

// Short bio links: record attribution (also done on full page load) then go home, keeping the query.
const ShortLinkRedirect = () => {
  const location = useLocation();
  applyShortLinkAttribution(location.pathname, location.search);
  return <Navigate to={{ pathname: "/", search: location.search }} replace />;
};

const CatchAllRedirect = () => {
  const location = useLocation();
  return <Navigate to={{ pathname: "/", search: location.search }} replace />;
};

// On first authenticated session, flush queued anon analytics and attribute UTMs.
const PostAuthBootstrap = () => {
  const { user } = useAuth();
  useEffect(() => {
    if (!user) return;
    (async () => {
      await flushAnonymousEvents();
      const attr = getAttribution();
      const quizReferred =
        typeof window !== "undefined" &&
        localStorage.getItem("gr_quiz_referred") === "1";
      const updatePayload: Record<string, unknown> = {};
      if (attr.source) updatePayload.source = attr.source;
      if (attr.campaign) updatePayload.campaign = attr.campaign;
      if (attr.content) updatePayload.content = attr.content;
      if (attr.hook) updatePayload.hook = attr.hook;
      if (attr.first_touch) updatePayload.first_touch = attr.first_touch;
      if (quizReferred) {
        updatePayload.referral_source = "quiz";
        // Ensure quiz signups appear in the standard Attribution Console alongside
        // other campaigns, even if utm capture was skipped (e.g. first-touch already
        // set from an earlier visit before the user hit the quiz).
        if (!updatePayload.source) updatePayload.source = "quiz";
        if (!updatePayload.campaign) updatePayload.campaign = "spot_the_pattern";
      }
      if (Object.keys(updatePayload).length > 0) {
        try {
          await supabase
            .from("profiles" as any)
            .update(updatePayload as any)
            .eq("user_id", user.id);
        } catch {}
      }
      trackEvent("signup_completed", { has_utms: !!attr.source, quiz_referred: quizReferred });
    })();
  }, [user]);
  return null;
};

const queryClient = new QueryClient();

// Protected route wrapper - redirects unauthenticated users to home
const ProtectedRoute = ({ children }: { children: React.ReactElement }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div
        className="min-h-screen bg-background"
        style={{ background: "linear-gradient(180deg, hsl(0 0% 10%) 0%, hsl(0 0% 5%) 100%)" }}
      />
    );
  }

  if (!user) {
    return <Navigate to="/" replace />;
  }

  return children;
};

// Root route handler - redirects based on auth state
const RootRoute = () => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div
        className="min-h-screen bg-background"
        style={{ background: "linear-gradient(180deg, hsl(0 0% 10%) 0%, hsl(0 0% 5%) 100%)" }}
      />
    );
  }

  // Allow previewing the landing page while signed in via ?preview=landing
  const previewLanding = typeof window !== "undefined" &&
    new URLSearchParams(window.location.search).get("preview") === "landing";

  if (user && !previewLanding) {
    // Check for a stored return URL (from sign-up email confirm, OAuth, or login)
    // Check both sessionStorage (same-session login) and localStorage (email confirm redirect)
    const returnUrl = sessionStorage.getItem("greyrock_return_url") || localStorage.getItem("greyrock_return_url");
    const pendingAnalysis = localStorage.getItem("greyrock_pending_analysis");

    // Clean up stored values
    sessionStorage.removeItem("greyrock_return_url");
    localStorage.removeItem("greyrock_return_url");

    if (returnUrl && returnUrl !== "/" && returnUrl !== "/login") {
      return <Navigate to={returnUrl} replace />;
    }

    // If there's a pending analysis message (from pre-signup), redirect to analysis-result
    if (pendingAnalysis) {
      localStorage.removeItem("greyrock_pending_analysis");
      return <Navigate to="/analysis-result" state={{ message: pendingAnalysis }} replace />;
    }

    return <Navigate to="/dashboard" replace />;
  }

  return <CinematicLanding />;
};

// Command Center routes
const AppRoutes = () => {
  return (
    <Routes>
      {/* Reset password route - always accessible without protection */}
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/login" element={<Login />} />

      {/* Home route - explicitly bound to the current landing page flow */}
      <Route path="/" element={<RootRoute />} />

      {/* Practice — primary simulation-first home for authenticated users */}
      <Route
        path="/practice"
        element={
          <ProtectedRoute>
            <Practice />
          </ProtectedRoute>
        }
      />
      <Route path="/app" element={<Navigate to="/practice" replace />} />
      

      {/* Dashboard / Analyze — secondary, preserved */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <Overview />
          </ProtectedRoute>
        }
      />
      <Route
        path="/analyze"
        element={
          <ProtectedRoute>
            <Overview />
          </ProtectedRoute>
        }
      />

      {/* Command Center Routes - Protected */}
      <Route
        path="/vault"
        element={
          <ProtectedRoute>
            <Vault />
          </ProtectedRoute>
        }
      />
      <Route
        path="/reports"
        element={
          <ProtectedRoute>
            <Reports />
          </ProtectedRoute>
        }
      />
      <Route path="/neutralizer" element={<Navigate to="/dashboard" replace />} />
      <Route
        path="/trends"
        element={
          <ProtectedRoute>
            <TrendsDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <SettingsPage />
          </ProtectedRoute>
        }
      />

      {/* Admin routes */}
      <Route path="/admin" element={<AdminPortal />} />
      <Route path="/system-admin" element={<AdminPortal />} />
      <Route path="/admin-portal" element={<AdminPortal />} />
      <Route path="/admin/viral/assets/:id" element={<AdminAssetDetail />} />
      <Route path="/admin/intelligence" element={<AdminIntelligence />} />
      <Route path="/admin/analytics" element={<Suspense fallback={<div className="min-h-screen bg-[#0a0a0f]" />}><AdminAnalytics /></Suspense>} />
      <Route path="/admin/content-engine" element={<SocialWarRoom />} />
      <Route path="/admin/content-library" element={<ContentLibrary />} />
      <Route path="/admin-lab" element={<AdminLab />} />
      <Route
        path="/library"
        element={
          <ProtectedRoute>
            <Library />
          </ProtectedRoute>
        }
      />
      <Route
        path="/archetypes"
        element={
          <ProtectedRoute>
            <ArchetypeArchive />
          </ProtectedRoute>
        }
      />

      {/* Public pages - accessible to all */}
      <Route path="/legal" element={<Legal />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/refunds" element={<Refunds />} />
      <Route path="/refund-policy" element={<Navigate to="/refunds" replace />} />
      <Route path="/about" element={<About />} />
      <Route path="/contact" element={<Contact />} />
      <Route path="/billing-fresh" element={<FreshBilling />} />
      <Route path="/pricing" element={<Pricing />} />
      <Route path="/analysis-result" element={<AnalysisResult />} />
      <Route path="/try" element={<FreeAnalysisResult />} />
      <Route path="/grey-rock-method-examples" element={<GreyRockMethodExamples />} />
      <Route path="/ai-detect-narcissistic-abuse" element={<AiDetectNarcissisticAbuse />} />
      <Route path="/chatgpt-vs-greyrock-ai" element={<ChatgptVsGreyrockAi />} />
      <Route path="/free-gaslighting-detector" element={<FreeGaslightingDetector />} />
      <Route path="/why-do-i-feel-exhausted-after-conversations" element={<WhyExhaustedAfterConversations />} />
      <Route path="/reactive-abuse-examples" element={<ReactiveAbuseExamples />} />
      <Route path="/why-do-i-look-like-the-crazy-one" element={<WhyILookLikeTheCrazyOne />} />
      <Route path="/why-do-they-disappear-and-come-back" element={<WhyDoTheyDisappearAndComeBack />} />
      <Route path="/why-do-i-feel-like-im-walking-on-eggshells" element={<WhyWalkingOnEggshells />} />
      <Route path="/darvo-examples" element={<DarvoExamples />} />
      <Route path="/emotional-invalidation-examples" element={<EmotionalInvalidationExamples />} />
      <Route path="/manipulative-text-message-analysis" element={<ManipulativeTextMessageAnalysis />} />
      <Route path="/articulation-before-adjudication" element={<ArticulationBeforeAdjudication />} />
      <Route path="/overthinking-vs-pattern-recognition" element={<OverthinkingVsPatternRecognition />} />
      <Route path="/analysis/criticism-patterns-in-text-messages" element={<CriticismPatternsInTextMessages />} />
      <Route path="/analysis/invalidation-patterns-in-texts" element={<InvalidationPatternsInTexts />} />
      <Route path="/analysis/darvo-in-text-messages" element={<DarvoInTextMessages />} />

      <Route path="/resources" element={<Resources />} />
      <Route path="/spot-the-pattern" element={<SpotThePattern />} />
      <Route path="/feed" element={<Navigate to="/" replace />} />
      <Route
        path="/neutralizer-lab/:id"
        element={
          <ProtectedRoute>
            <NeutralizerLabDetail />
          </ProtectedRoute>
        }
      />
      <Route
        path="/rehearsal"
        element={
          <ProtectedRoute>
            <RehearsalChat />
          </ProtectedRoute>
        }
      />
      <Route
        path="/rehearsal/debrief"
        element={
          <ProtectedRoute>
            <RehearsalDebrief />
          </ProtectedRoute>
        }
      />
      <Route
        path="/rehearsal/debrief/:sessionId"
        element={
          <ProtectedRoute>
            <RehearsalDebrief />
          </ProtectedRoute>
        }
      />
      <Route
        path="/rehearsal/archive"
        element={
          <ProtectedRoute>
            <RehearsalArchive />
          </ProtectedRoute>
        }
      />

      {/* Creator pages — no auth required */}
      <Route path="/portal/:promoCode" element={<CreatorPortal />} />

      <Route path="/professionals" element={<Professionals />} />
      {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
      <Route path="/tt" element={<ShortLinkRedirect />} />
      <Route path="/yt" element={<ShortLinkRedirect />} />
      <Route path="/ig" element={<ShortLinkRedirect />} />
      <Route path="/pin" element={<ShortLinkRedirect />} />
      <Route path="*" element={<CatchAllRedirect />} />
    </Routes>
  );
};

const App = () => (
  <HelmetProvider>
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter>
            <PostAuthBootstrap />
            <AppRoutes />
          </BrowserRouter>
        </TooltipProvider>
      </AuthProvider>
    </QueryClientProvider>
  </HelmetProvider>
);

export default App;
